/**
 * 3D Carousel Library
 * A modern 3D carousel component for the We Ghost theme
 */

class Carousel3D {
    constructor(container, options = {}) {
        if (!container) {
            console.warn('Carousel3D: No container element provided');
            return;
        }

        this.container = container;
        this.currentIndex = 0;
        this.cards = container.querySelectorAll('.carousel-card');
        this.totalCards = this.cards.length;
        this.isTransitioning = false;

        // Default options
        this.options = {
            autoplay: false,
            autoplayDelay: 4000,
            keyboard: true,
            touch: true,
            loop: true,
            startIndex: 0,
            onSlideChange: null,
            ...options
        };

        if (this.totalCards === 0) {
            console.warn('Carousel3D: No carousel cards found');
            return;
        }

        this.init();
    }

    init() {
        this.currentIndex = this.options.startIndex;
        this.setupEventListeners();
        this.updateCarousel();

        if (this.options.autoplay) {
            this.startAutoplay();
        }
    }

    setupEventListeners() {
        // Navigation buttons
        const prevBtn = this.container.querySelector('.carousel-nav.prev');
        const nextBtn = this.container.querySelector('.carousel-nav.next');

        if (prevBtn) {
            prevBtn.addEventListener('click', () => this.prev());
        }

        if (nextBtn) {
            nextBtn.addEventListener('click', () => this.next());
        }

        // Card clicks - go to clicked card
        this.cards.forEach((card, index) => {
            card.addEventListener('click', () => this.goTo(index));

            // Add keyboard support for cards
            card.setAttribute('tabindex', '0');
            card.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.goTo(index);
                }
            });
        });

        // Keyboard navigation
        if (this.options.keyboard) {
            document.addEventListener('keydown', (e) => {
                // Only handle if carousel is in focus or no other input is focused
                if (document.activeElement &&
                    (document.activeElement.tagName === 'INPUT' ||
                     document.activeElement.tagName === 'TEXTAREA' ||
                     document.activeElement.isContentEditable)) {
                    return;
                }

                if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    this.prev();
                } else if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    this.next();
                }
            });
        }

        // Touch/swipe support
        if (this.options.touch) {
            this.setupTouchEvents();
        }

        // Pause autoplay on hover
        if (this.options.autoplay) {
            this.container.addEventListener('mouseenter', () => this.stopAutoplay());
            this.container.addEventListener('mouseleave', () => this.startAutoplay());
        }

        // Handle visibility change (pause when tab is not active)
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                this.stopAutoplay();
            } else if (this.options.autoplay) {
                this.startAutoplay();
            }
        });
    }

    setupTouchEvents() {
        let startX = 0;
        let endX = 0;
        let startY = 0;
        let endY = 0;
        let isTouch = false;

        this.container.addEventListener('touchstart', (e) => {
            startX = e.touches[0].clientX;
            startY = e.touches[0].clientY;
            isTouch = true;
        }, { passive: true });

        this.container.addEventListener('touchmove', (e) => {
            if (!isTouch) return;

            endX = e.touches[0].clientX;
            endY = e.touches[0].clientY;

            // Prevent vertical scroll if horizontal swipe is detected
            const diffX = Math.abs(startX - endX);
            const diffY = Math.abs(startY - endY);

            if (diffX > diffY && diffX > 10) {
                e.preventDefault();
            }
        }, { passive: false });

        this.container.addEventListener('touchend', (e) => {
            if (!isTouch) return;

            const diffX = startX - endX;
            const diffY = startY - endY;

            // Only handle horizontal swipes
            if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
                if (diffX > 0) {
                    this.next();
                } else {
                    this.prev();
                }
            }

            isTouch = false;
        }, { passive: true });
    }

    getCardPosition(index) {
        const diff = index - this.currentIndex;
        const total = this.totalCards;

        // Normalize the difference to handle wrapping
        let normalizedDiff = diff;
        if (this.options.loop && Math.abs(diff) > total / 2) {
            normalizedDiff = diff > 0 ? diff - total : diff + total;
        }

        switch (normalizedDiff) {
            case 0: return 'active';
            case -1: return 'prev';
            case 1: return 'next';
            case -2: return 'hidden-left';
            case 2: return 'hidden-right';
            case -3:
            case -4: return 'far-left';
            case 3:
            case 4: return 'far-right';
            default:
                return normalizedDiff < 0 ? 'far-left' : 'far-right';
        }
    }

    updateCarousel() {
        if (this.isTransitioning) return;

        this.isTransitioning = true;

        this.cards.forEach((card, index) => {
            // Remove all position classes
            card.classList.remove('active', 'prev', 'next', 'hidden-left', 'hidden-right', 'far-left', 'far-right');

            // Add the appropriate position class
            const position = this.getCardPosition(index);
            card.classList.add(position);

            // Update aria attributes for accessibility
            card.setAttribute('aria-hidden', position !== 'active');
            if (position === 'active') {
                card.setAttribute('aria-current', 'true');
            } else {
                card.removeAttribute('aria-current');
            }
        });

        // Call callback if provided
        if (typeof this.options.onSlideChange === 'function') {
            this.options.onSlideChange(this.currentIndex, this.cards[this.currentIndex]);
        }

        // Reset transition flag after animation completes
        setTimeout(() => {
            this.isTransitioning = false;
        }, 300);
    }

    goTo(index) {
        if (this.isTransitioning || index === this.currentIndex) return;

        // Validate index
        if (index < 0 || index >= this.totalCards) return;

        this.currentIndex = index;
        this.updateCarousel();
    }

    next() {
        if (this.isTransitioning) return;

        if (this.options.loop) {
            this.currentIndex = (this.currentIndex + 1) % this.totalCards;
        } else {
            this.currentIndex = Math.min(this.currentIndex + 1, this.totalCards - 1);
        }

        this.updateCarousel();
    }

    prev() {
        if (this.isTransitioning) return;

        if (this.options.loop) {
            this.currentIndex = (this.currentIndex - 1 + this.totalCards) % this.totalCards;
        } else {
            this.currentIndex = Math.max(this.currentIndex - 1, 0);
        }

        this.updateCarousel();
    }

    startAutoplay() {
        if (!this.options.autoplay) return;

        this.stopAutoplay(); // Clear any existing autoplay

        this.autoplayTimer = setInterval(() => {
            this.next();
        }, this.options.autoplayDelay);
    }

    stopAutoplay() {
        if (this.autoplayTimer) {
            clearInterval(this.autoplayTimer);
            this.autoplayTimer = null;
        }
    }

    // Public API methods
    getCurrentIndex() {
        return this.currentIndex;
    }

    getTotalCards() {
        return this.totalCards;
    }

    destroy() {
        this.stopAutoplay();

        // Remove event listeners
        this.cards.forEach(card => {
            card.removeAttribute('tabindex');
            card.removeAttribute('aria-hidden');
            card.removeAttribute('aria-current');
        });

        // Reset classes
        this.cards.forEach(card => {
            card.classList.remove('active', 'prev', 'next', 'hidden-left', 'hidden-right', 'far-left', 'far-right');
        });
    }
}

// Auto-initialize carousels when DOM is ready
function initCarousels() {
    const carousels = document.querySelectorAll('.carousel-3d');
    const carouselInstances = [];

    carousels.forEach(carousel => {
        // Get options from data attributes
        const options = {
            autoplay: carousel.dataset.autoplay === 'true',
            autoplayDelay: parseInt(carousel.dataset.autoplayDelay) || 4000,
            keyboard: carousel.dataset.keyboard !== 'false',
            touch: carousel.dataset.touch !== 'false',
            loop: carousel.dataset.loop !== 'false',
            startIndex: parseInt(carousel.dataset.startIndex) || 0
        };

        const instance = new Carousel3D(carousel, options);
        carouselInstances.push(instance);

        // Store instance on element for external access
        carousel.carousel3d = instance;
    });

    return carouselInstances;
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
    module.exports = { Carousel3D, initCarousels };
}

// Global initialization function
window.initCarousel3D = initCarousels;
window.Carousel3D = Carousel3D;